import { useState, useEffect, useMemo } from "react"
import { Watch, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  getWearablesOverview,
  getWearableConnections,
  getWearableUserDetail,
  type WearableProviderReport,
  type WearableConnectionRow,
  type WearableDataType,
  type WearableUserDetail,
  type WearableReading,
} from "@/lib/api"
import { cn } from "@/lib/utils"

const PROVIDERS = ["OURA", "WHOOP", "GARMIN", "EIGHTSLEEP", "APPLE_HEALTH"] as const
type Provider = (typeof PROVIDERS)[number]

const PROVIDER_LABELS: Record<Provider, string> = {
  OURA: "Oura",
  WHOOP: "Whoop",
  GARMIN: "Garmin",
  EIGHTSLEEP: "Eight Sleep",
  APPLE_HEALTH: "Apple Health",
}

function formatDate(value: string | null): string {
  if (!value) return "-"
  const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function daysSince(value: string | null): number | null {
  if (!value) return null
  const d = new Date(value.length <= 10 ? `${value}T00:00:00` : value)
  if (Number.isNaN(d.getTime())) return null
  return Math.floor((Date.now() - d.getTime()) / 86400000)
}

/** Cap JSON preview length so long sample arrays don't hide shorter keys below. */
function previewJson(value: unknown, limit = 1400): string {
  const text = JSON.stringify(value, null, 2) ?? "null"
  return text.length > limit ? `${text.slice(0, limit)}\n... truncated` : text
}

/** Mirrors backend _flatten: dotted leaves, arrays as item count. */
function flattenPayload(
  payload: unknown,
  prefix = "",
  depth = 0,
): Record<string, unknown> {
  if (typeof payload !== "object" || payload === null || depth >= 3) return {}
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    const path = `${prefix}${key}`
    if (Array.isArray(value)) {
      out[path] = value.length ? `${value.length} items` : null
    } else if (typeof value === "object" && value !== null) {
      const nested = flattenPayload(value, `${path}.`, depth + 1)
      if (Object.keys(nested).length) Object.assign(out, nested)
      else out[path] = null
    } else {
      out[path] = value
    }
  }
  return out
}

function formatExample(value: unknown): string {
  if (value === null || value === undefined) return "-"
  if (typeof value === "number") return String(Math.round(value * 100) / 100)
  if (typeof value === "string") return value.length > 28 ? `${value.slice(0, 28)}...` : value
  return JSON.stringify(value)
}

/** Field present on every sample but always null (e.g. Whoop distance_data.steps). */
function isAlwaysNull(pct: number, seen: number): boolean {
  return pct === 0 && seen > 0
}

/** Renders payload leaves generically so unanticipated fields still surface. */
function UserReadings({
  detail,
  onClose,
}: {
  detail: WearableUserDetail
  onClose: () => void
}) {
  const [rawFor, setRawFor] = useState<string | null>(null)

  const byDate = useMemo(() => {
    const map = new Map<string, WearableReading[]>()
    for (const r of detail.rows) {
      const list = map.get(r.recorded_at) ?? []
      list.push(r)
      map.set(r.recorded_at, list)
    }
    return [...map.entries()]
  }, [detail.rows])

  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/30 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold">
            {detail.user.display_name ?? "Unnamed user"}
          </h3>
          <p className="font-mono text-[11px] text-muted-foreground">
            {detail.user.id}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            {detail.rows.length} reading{detail.rows.length === 1 ? "" : "s"}
            {detail.truncated && ` (newest ${detail.row_limit})`}
          </span>
          <button onClick={onClose} className="rounded border px-2 py-1 hover:bg-accent">
            Close
          </button>
        </div>
      </div>

      {detail.rows.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">
          No stored readings for this user on this provider.
        </p>
      ) : (
        <div className="max-h-128 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-background">
              <tr className="border-b text-xs text-muted-foreground">
                <th className="px-4 py-2 text-left font-medium">Date</th>
                <th className="px-4 py-2 text-left font-medium">Type</th>
                <th className="px-4 py-2 text-left font-medium">Values</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {byDate.map(([date, readings]) =>
                readings.map((r, i) => {
                  const rowKey = `${date}-${r.data_type}`
                  const leaves = Object.entries(flattenPayload(r.payload)).filter(
                    ([, v]) => v !== null && v !== undefined,
                  )
                  return (
                    <tr key={rowKey} className="border-b align-top last:border-0">
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-muted-foreground">
                        {i === 0 ? formatDate(date) : ""}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">
                        {r.data_type}
                      </td>
                      <td className="px-4 py-2">
                        {leaves.length === 0 ? (
                          <span className="text-xs italic text-amber-600">
                            stored, but every field is empty
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {leaves.map(([k, v]) => (
                              <span key={k} className="font-mono text-xs">
                                <span className="text-muted-foreground">{k}=</span>
                                {formatExample(v)}
                              </span>
                            ))}
                          </div>
                        )}
                        {rawFor === rowKey && (
                          <pre className="mt-2 overflow-x-auto rounded bg-muted/40 p-3 text-[11px] leading-relaxed">
                            {previewJson(r.payload)}
                          </pre>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => setRawFor(rawFor === rowKey ? null : rowKey)}
                          className="rounded border px-2 py-0.5 text-[11px] hover:bg-accent"
                        >
                          {rawFor === rowKey ? "hide" : "raw"}
                        </button>
                      </td>
                    </tr>
                  )
                }),
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function DataTypeCard({ dt }: { dt: WearableDataType }) {
  const [showRaw, setShowRaw] = useState(false)
  const missing = dt.fields.filter((f) => isAlwaysNull(f.populated_pct, f.seen))

  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-muted/30 px-4 py-3">
        <div className="flex items-center gap-3">
          <h3 className="font-mono text-sm font-semibold">{dt.data_type}</h3>
          <span className="text-xs text-muted-foreground">
            {dt.rows_sampled} rows &middot; {dt.users} {dt.users === 1 ? "user" : "users"}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>
            {formatDate(dt.oldest)} to {formatDate(dt.newest)}
          </span>
          <button
            onClick={() => setShowRaw((v) => !v)}
            className="rounded border px-2 py-1 hover:bg-accent"
          >
            {showRaw ? "Hide" : "Show"} sample
          </button>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="flex items-start gap-2 border-b bg-amber-50 px-4 py-2 dark:bg-amber-950/20">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
          <p className="text-xs text-amber-800 dark:text-amber-500">
            Sent but always empty:{" "}
            <span className="font-mono">{missing.map((f) => f.key).join(", ")}</span>
          </p>
        </div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="px-4 py-2 text-left font-medium">Field</th>
            <th className="px-4 py-2 text-right font-medium">Populated</th>
            <th className="px-4 py-2 text-left font-medium">Example</th>
          </tr>
        </thead>
        <tbody>
          {dt.fields.map((f) => {
            const empty = isAlwaysNull(f.populated_pct, f.seen)
            return (
              <tr key={f.key} className="border-b last:border-0">
                <td
                  className={cn(
                    "px-4 py-1.5 font-mono text-xs",
                    empty && "text-muted-foreground line-through",
                  )}
                >
                  {f.key}
                </td>
                <td className="px-4 py-1.5 text-right tabular-nums">
                  <span
                    className={cn(
                      "text-xs",
                      empty
                        ? "text-amber-600"
                        : f.populated_pct === 100
                          ? "text-emerald-600"
                          : "text-muted-foreground",
                    )}
                  >
                    {f.populated_pct}%
                  </span>
                </td>
                <td className="px-4 py-1.5 font-mono text-xs text-muted-foreground">
                  {formatExample(f.example)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {showRaw && (
        <pre className="overflow-x-auto border-t bg-muted/40 p-4 text-xs leading-relaxed">
          {previewJson(dt.sample_payload)}
        </pre>
      )}
    </div>
  )
}

function ConnectionsTable({
  rows,
  selectedUserId,
  onSelect,
}: {
  rows: WearableConnectionRow[]
  selectedUserId: string | null
  onSelect: (userId: string) => void
}) {
  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
        No connections for this provider yet.
      </p>
    )
  }

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
            <th className="px-4 py-2 text-left font-medium">User</th>
            <th className="px-4 py-2 text-left font-medium">Connected</th>
            <th className="px-4 py-2 text-left font-medium">Newest reading</th>
            <th className="px-4 py-2 text-left font-medium">State</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const gap = daysSince(c.latest_data_at)
            // Synced with mobile RECONNECT_AFTER_DAYS — gap this long means broken link.
            const broken = gap === null || gap >= 14
            const selected = c.user_id === selectedUserId
            return (
              <tr
                key={c.id}
                onClick={() => onSelect(c.user_id)}
                aria-selected={selected}
                className={cn(
                  "cursor-pointer border-b last:border-0 hover:bg-accent/50",
                  selected && "bg-accent",
                )}
              >
                <td className="px-4 py-2">
                  <span className="text-xs font-medium">
                    {c.display_name ?? "Unnamed"}
                  </span>
                  <span className="ml-2 font-mono text-[11px] text-muted-foreground">
                    {c.user_id.slice(0, 8)}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-muted-foreground">
                  {formatDate(c.connected_at)}
                </td>
                <td className="px-4 py-2 text-xs">
                  {c.latest_data_at ? (
                    <span className={cn(broken && "text-amber-600")}>
                      {formatDate(c.latest_data_at)}
                      {gap !== null && gap > 1 && (
                        <span className="ml-1 text-muted-foreground">({gap}d ago)</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-amber-600">never</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  {!c.is_active ? (
                    <span className="text-xs text-muted-foreground">inactive</span>
                  ) : broken ? (
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                      <AlertTriangle className="size-3" /> needs reconnect
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                      <CheckCircle2 className="size-3" /> healthy
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export function WearablesPage() {
  const [overview, setOverview] = useState<Record<string, WearableProviderReport>>({})
  const [connections, setConnections] = useState<WearableConnectionRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Provider>("OURA")
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [detail, setDetail] = useState<WearableUserDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const fetchData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [ov, conns] = await Promise.all([
        getWearablesOverview(),
        getWearableConnections(),
      ])
      setOverview(ov)
      setConnections(conns)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load wearable data")
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  // Readings are per-provider — clear stale detail on tab switch.
  useEffect(() => {
    if (!selectedUserId) {
      setDetail(null)
      setDetailError(null)
      return
    }
    let cancelled = false
    setDetailLoading(true)
    setDetailError(null)
    getWearableUserDetail(selectedUserId, tab)
      .then((d) => {
        if (!cancelled) setDetail(d)
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetail(null)
          setDetailError(
            err instanceof Error ? err.message : "Failed to load readings",
          )
        }
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedUserId, tab])

  // Diff provider types against union so never-sent types (e.g. Whoop steps) are explicit.
  const missingByProvider = useMemo(() => {
    const universe = new Set<string>()
    for (const report of Object.values(overview)) {
      for (const dt of report.data_types) universe.add(dt.data_type)
    }
    const map: Record<string, string[]> = {}
    for (const [prov, report] of Object.entries(overview)) {
      const have = new Set(report.data_types.map((dt) => dt.data_type))
      map[prov] = [...universe].filter((t) => !have.has(t)).sort()
    }
    return map
  }, [overview])

  const connectionsByProvider = useMemo(() => {
    const map: Record<string, WearableConnectionRow[]> = {}
    for (const c of connections) {
      ;(map[c.provider] ??= []).push(c)
    }
    return map
  }, [connections])

  if (isLoading) {
    return (
      <div className="p-6">
        <h1 className="mb-6 text-2xl font-bold">Wearables</h1>
        <div className="space-y-4">
          <div className="h-24 animate-pulse rounded-lg bg-muted" />
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="mb-4 text-2xl font-bold">Wearables</h1>
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <AlertTriangle className="mx-auto mb-2 size-8 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Watch className="size-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold">Wearables</h1>
            <p className="text-sm text-muted-foreground">
              What each provider actually sends us, field by field.
            </p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          <RefreshCw className="size-3.5" />
          Refresh
        </button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Provider)}>
        <TabsList className="mb-4">
          {PROVIDERS.map((p) => {
            const count = connectionsByProvider[p]?.length ?? 0
            return (
              <TabsTrigger key={p} value={p}>
                {PROVIDER_LABELS[p]}
                <span className="ml-1.5 text-xs text-muted-foreground">{count}</span>
              </TabsTrigger>
            )
          })}
        </TabsList>

        {PROVIDERS.map((p) => {
          const report = overview[p]
          const rows = connectionsByProvider[p] ?? []
          return (
            <TabsContent key={p} value={p} className="space-y-6">
              <section className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Connections
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Select a user to see their stored readings.
                  </p>
                </div>
                <ConnectionsTable
                  rows={rows}
                  selectedUserId={selectedUserId}
                  onSelect={(id) =>
                    setSelectedUserId((cur) => (cur === id ? null : id))
                  }
                />

                {detailLoading && (
                  <div className="h-40 animate-pulse rounded-lg bg-muted" />
                )}
                {detailError && !detailLoading && (
                  <div className="rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                    {detailError}
                  </div>
                )}
                {detail && !detailLoading && !detailError && (
                  <UserReadings
                    detail={detail}
                    onClose={() => setSelectedUserId(null)}
                  />
                )}
              </section>

              <section className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    Data we receive
                  </h2>
                  {report?.truncated && (
                    <p className="text-xs text-muted-foreground">
                      Sampled the {report.sample_limit} most recent rows.
                    </p>
                  )}
                </div>

                {missingByProvider[p]?.length > 0 && (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 dark:border-amber-900 dark:bg-amber-950/20">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
                    <p className="text-xs text-amber-800 dark:text-amber-500">
                      Never sent by {PROVIDER_LABELS[p]}, but other providers send it:{" "}
                      <span className="font-mono">{missingByProvider[p].join(", ")}</span>
                    </p>
                  </div>
                )}

                {!report || report.data_types.length === 0 ? (
                  <p className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
                    {PROVIDER_LABELS[p]} has not sent any data yet.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {report.data_types.map((dt) => (
                      <DataTypeCard key={dt.data_type} dt={dt} />
                    ))}
                  </div>
                )}
              </section>
            </TabsContent>
          )
        })}
      </Tabs>
    </div>
  )
}
